import { router } from 'expo-router';
import { useAtomValue } from 'jotai';
import { useDebouncedCallback } from 'use-debounce';

import { buildInquiryChatParams } from '@/components/inbox/inquiryChatParams';
import { useAnonymousInquiryGroupingEnabled } from '@/hooks/inbox/useGroupedInquiriesEnabled';
import { abilitySubject, useAbility } from '@/hooks/useAbility';
import { useIsCompactLayout } from '@/hooks/useResponsiveLayout';
import type { InquiryV2 } from '@/services/getInquiriesV2';
import { userIdAtom } from '@/services/globalState';
import { isSameUserId } from '@/utils/isSameUserId';

/**
 * Opens an inquiry's conversation the way a flat inbox row does: the same route params, a push on
 * a compact layout and a param update beside the list on desktop. Debounced so a double tap opens
 * one chat.
 */
export function useOpenInquiryChat(currentTab: string) {
  const effectiveUserId = useAtomValue(userIdAtom);

  const isAnonymousInquiryGroupingEnabled = useAnonymousInquiryGroupingEnabled();

  const isCompactLayout = useIsCompactLayout();

  const ability = useAbility();

  return useDebouncedCallback(
    (inquiry: InquiryV2, groupConversationIds: string[]) => {
      const canMarkOnYourHonor =
        !isSameUserId(inquiry.author?.id, effectiveUserId) &&
        inquiry.firstRespondedAt != null &&
        ability.can('attest', abilitySubject('Inquiry', inquiry));

      const chatParams = buildInquiryChatParams(inquiry, currentTab, {
        effectiveUserId,
        isAnonymousInquiryGroupingEnabled,
        canMarkOnYourHonor,
        groupConversationIds,
      });

      if (!chatParams) {
        return;
      }

      if (isCompactLayout) {
        router.push({ pathname: '/inbox/chat/[id]', params: chatParams });

        return;
      }

      router.setParams(chatParams);
    },
    600,
    { leading: true, trailing: false }
  );
}
